'use client';

import {
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
  type TextFieldProps,
} from '@mui/material';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { InputErrorIcon } from '@/components/form/validation/inputErrorIcon';
import { Link } from '@/components/shared/link';
import { useEnvironment } from '@/contexts/useEnvironment';

import { WelcomePageWrapper } from '../../welcomePageWrapper';

export function DomainInput({
  inputProps,
  errorType,
  ...props
}: TextFieldProps & { errorType?: string }) {
  return (
    <div className='relative'>
      <TextField
        error={errorType === 'EON_ACCOUNT_NOT_FOUND'}
        size='medium'
        placeholder='your-company'
        inputProps={{
          ...inputProps,
          id: 'login-domain',
          style: { height: '36px', paddingRight: 130 },
        }}
        {...props}
      />
      <Stack
        direction='row'
        alignItems='center'
        gap='4px'
        sx={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          right: 12,
        }}
      >
        {errorType === 'EON_ACCOUNT_NOT_FOUND' && (
          <InputErrorIcon errorMessage={errors[errorType]} />
        )}
        <Typography>.console.eon.io</Typography>
      </Stack>
    </div>
  );
}

const errors: Record<string, string> = {
  EON_ACCOUNT_NOT_FOUND: 'Eon account not found for domain',
  PROJECTS_ERROR: 'Failed to get projects',
  JWT_SECRET_MISSING: 'JWT is missing',
  JWT_SIGNING_ERROR: 'Failed to sign jwt',
  AUTH_HEADER_ERROR: 'Failed to set auth header',
};

export function LoginPage() {
  const [domain, setDomain] = useState('');
  const [focused, setFocused] = useState(true);
  const { clientApiBaseUrl, isDev, isTest } = useEnvironment();
  const searchParams = useSearchParams();

  const errorType = searchParams.get('error');

  useEffect(() => {
    if (isDev && !isTest) {
      const urlDomain = searchParams.get('domain');
      onLogin(urlDomain || 'eon-team');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = (domain: string) => {
    if (!domain) return;
    let url = `${clientApiBaseUrl}/login?domain=${domain}`;
    const returnUrl = searchParams.get('returnUrl');

    if (!isDev && returnUrl) {
      url += `&returnUrl=${returnUrl}`;
    }

    window.location.replace(url);
  };

  return (
    <WelcomePageWrapper>
      <Stack gap='40px' overflow='hidden'>
        <Typography
          sx={{ wordWrap: 'break-word' }}
          maxWidth='540px'
          variant='h1'
        >
          Log in with your account’s domain
        </Typography>
        <Stack direction='row' alignItems='center' gap='16px'>
          <DomainInput
            autoFocus
            errorType={errorType || undefined}
            focused={focused}
            onBlur={() => setFocused(false)}
            onFocus={() => setFocused(true)}
            inputProps={{ 'data-testid': 'login-domain' }}
            value={domain}
            sx={{ width: '320px' }}
            onChange={(e) => {
              const newValue = e.target.value;

              // allow only alphanumeric characters, hyphens, and periods
              if (/^[a-zA-Z0-9-]*$/.test(newValue)) {
                setDomain(newValue);
              }
            }}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                onLogin(domain);
              }
            }}
          />
          <Button
            className='h-full'
            disabled={!domain}
            data-testid='login-button'
            variant='contained'
            onClick={() => onLogin(domain)}
          >
            Login
            <i className='material-symbols-arrow-right-alt ml-[8px] align-middle' />
          </Button>
        </Stack>
        {errorType && errorType !== 'EON_ACCOUNT_NOT_FOUND' && (
          <Stack direction='row' alignItems='center'>
            <i className='material-symbols-error-outline-rounded mr-[8px] text-[var(--mui-palette-error-main)]'></i>
            <Typography
              variant='body1'
              color='error'
              data-testid='error-message'
            >
              {errors[errorType] || 'Unknown error'}
            </Typography>
          </Stack>
        )}
        <Divider
          sx={{
            borderStyle: 'dashed',
            width: 600,
            borderColor: 'var(--mui-palette-background-lightGray)',
          }}
        />
        <Stack direction='row' alignItems='center' gap='8px'>
          {`Don't have an account? `}
          <Link
            className='font-bold'
            href='https://www.eon.io/get-a-demo'
            target='_blank'
          >
            <Button variant='text'>Sign Up</Button>
          </Link>
        </Stack>
      </Stack>
    </WelcomePageWrapper>
  );
}
